import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Eye, Trash2, MessageSquare, Phone, Mail, FileText, Clock, CheckCircle, XCircle, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  new: { label: 'Nouveau', color: 'bg-blue-100 text-blue-800', icon: Clock },
  contacted: { label: 'Contacté', color: 'bg-yellow-100 text-yellow-800', icon: Phone },
  quoted: { label: 'Devis envoyé', color: 'bg-purple-100 text-purple-800', icon: Send },
  converted: { label: 'Converti', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  closed: { label: 'Fermé', color: 'bg-gray-100 text-gray-800', icon: XCircle }
};

const AdminQuoteRequests = () => {
  const navigate = useNavigate();
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [editedNotes, setEditedNotes] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchQuoteRequests();
  }, [navigate]);

  useEffect(() => {
    const filtered = quoteRequests.filter(quote => {
      const searchLower = searchQuery.toLowerCase();
      return (quote.name || '').toLowerCase().includes(searchLower) ||
        (quote.email || '').toLowerCase().includes(searchLower) ||
        (quote.phone || '').toLowerCase().includes(searchLower) ||
        (quote.message || '').toLowerCase().includes(searchLower);
    });
    setFilteredRequests(filtered);
  }, [searchQuery, quoteRequests]);

  const fetchQuoteRequests = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/quote-request/admin/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuoteRequests(response.data);
      setFilteredRequests(response.data);
    } catch (error) {
      console.error('Error fetching quote requests:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewQuote = (quote) => {
    setSelectedQuote(quote);
    setEditedNotes(quote.notes || '');
    setShowModal(true);
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/quote-request/admin/${quoteId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchQuoteRequests();
      if (selectedQuote?.id === quoteId) {
        setSelectedQuote({ ...selectedQuote, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating quote status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleSaveNotes = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/quote-request/admin/${selectedQuote.id}`,
        { notes: editedNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedQuote({ ...selectedQuote, notes: editedNotes });
      fetchQuoteRequests();
      alert('Notes sauvegardées');
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Erreur lors de la sauvegarde des notes');
    }
  };

  const handleDeleteClick = (quote, e) => {
    e.stopPropagation();
    setQuoteToDelete(quote);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/quote-request/admin/${quoteToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowDeleteConfirm(false);
      setQuoteToDelete(null);
      if (selectedQuote?.id === quoteToDelete.id) {
        setShowModal(false);
      }
      await fetchQuoteRequests();
      alert('Demande supprimée avec succès');
    } catch (error) {
      console.error('Error deleting quote request:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Demandes de devis</h1>
                <p className="text-sm text-gray-600">{filteredRequests.length} demandes</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher par nom, email, téléphone ou message..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-celadon"
            />
          </div>
        </div>

        {/* Quote Requests Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fichiers</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredRequests.map((quote) => {
                const StatusIcon = statusConfig[quote.status]?.icon || Clock;
                return (
                  <tr key={quote.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatDate(quote.submitted_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{quote.name || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="flex items-center gap-1 text-gray-600">
                          <Mail size={14} />
                          <span>{quote.email}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500">
                          <Phone size={14} />
                          <span>{quote.phone}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 max-w-xs truncate">
                        {quote.message?.substring(0, 80)}{quote.message?.length > 80 ? '...' : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {quote.files && quote.files.length > 0 ? (
                        <span className="flex items-center gap-1 text-sm text-gray-600">
                          <FileText size={14} />
                          {quote.files.length}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`flex items-center gap-1 px-2 py-1 text-xs rounded-full w-fit ${statusConfig[quote.status]?.color || 'bg-gray-100 text-gray-800'}`}>
                        <StatusIcon size={14} />
                        {statusConfig[quote.status]?.label || quote.status || 'Nouveau'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewQuote(quote)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Voir les détails"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={(e) => handleDeleteClick(quote, e)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredRequests.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
              <p>Aucune demande de devis trouvée</p>
            </div>
          )}
        </div>
      </div>

      {/* Quote Details Modal */}
      {showModal && selectedQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold">Demande de devis</h2>
                <p className="text-gray-600 mt-1">{formatDate(selectedQuote.submitted_at)}</p>
              </div>
              <span className={`px-3 py-1 text-sm rounded-full ${statusConfig[selectedQuote.status]?.color || 'bg-gray-100'}`}>
                {statusConfig[selectedQuote.status]?.label || selectedQuote.status || 'Nouveau'}
              </span>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status Selector */}
              <div>
                <label className="block text-sm font-medium mb-2">Statut</label>
                <select
                  value={selectedQuote.status || 'new'}
                  onChange={(e) => handleStatusChange(selectedQuote.id, e.target.value)}
                  className="w-full px-4 py-2 border rounded focus:outline-none focus:border-celadon"
                >
                  <option value="new">Nouveau</option>
                  <option value="contacted">Contacté</option>
                  <option value="quoted">Devis envoyé</option>
                  <option value="converted">Converti (client)</option>
                  <option value="closed">Fermé</option>
                </select>
              </div>

              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-medium mb-2">Informations client</h3>
                  <div className="text-sm space-y-2 bg-gray-50 p-4 rounded">
                    <div>
                      <label className="text-xs text-gray-500">Nom:</label>
                      <p className="font-medium">{selectedQuote.name}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Email:</label>
                      <p className="font-medium">
                        <a href={`mailto:${selectedQuote.email}`} className="text-blue-600 hover:underline">
                          {selectedQuote.email}
                        </a>
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Téléphone:</label>
                      <p className="font-medium">
                        <a href={`tel:${selectedQuote.phone}`} className="text-blue-600 hover:underline">
                          {selectedQuote.phone}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Files */}
                <div>
                  <h3 className="font-medium mb-2">Fichiers joints ({selectedQuote.files?.length || 0})</h3>
                  <div className="bg-gray-50 p-4 rounded">
                    {selectedQuote.files && selectedQuote.files.length > 0 ? (
                      <ul className="text-sm space-y-2">
                        {selectedQuote.files.map((file, index) => (
                          <li key={index} className="flex items-center gap-2">
                            <FileText size={16} className="text-gray-500" />
                            <a 
                              href={file.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {file.original_name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500">Aucun fichier joint</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <h3 className="font-medium mb-2">Message du client</h3>
                <div className="bg-gray-50 p-4 rounded">
                  <p className="text-sm whitespace-pre-wrap">{selectedQuote.message}</p>
                </div>
              </div>

              {/* Notes */}
              <div>
                <h3 className="font-medium mb-2">Notes internes</h3>
                <textarea
                  value={editedNotes}
                  onChange={(e) => setEditedNotes(e.target.value)}
                  placeholder="Ajoutez vos notes ici..."
                  className="w-full px-4 py-3 border rounded focus:outline-none focus:border-celadon h-24"
                />
                <button
                  onClick={handleSaveNotes}
                  className="mt-2 px-4 py-2 bg-celadon text-white rounded hover:bg-celadon-light transition-colors"
                >
                  Sauvegarder les notes
                </button>
              </div>

              {/* Dates */}
              <div className="text-xs text-gray-500 border-t pt-4">
                <p>Reçue le: {formatDate(selectedQuote.submitted_at)}</p>
                {selectedQuote.updated_at && selectedQuote.updated_at !== selectedQuote.submitted_at && (
                  <p>Mise à jour: {formatDate(selectedQuote.updated_at)}</p>
                )}
              </div>
            </div>

            <div className="p-6 border-t flex gap-3">
              <button
                onClick={() => {
                  handleDeleteClick(selectedQuote, { stopPropagation: () => {} });
                }}
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded transition-colors"
              >
                Supprimer
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && quoteToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer la demande de <span className="font-medium">{quoteToDelete.name}</span> ?
              Cette action est irréversible.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded transition-colors"
              >
                Supprimer
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setQuoteToDelete(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuoteRequests;
